import {
  QueryHookOptions,
  QueryResult,
  TypedDocumentNode,
  useLazyQuery as apolloUseLazyQuery,
  useQuery as apolloUseQuery,
} from '@apollo/client'
import { type Exact } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

/**
 * This includes a few extra options that can be passed to the following custom hook
 * to handle query errors.
 */
export interface ExtraOptions {
  /**
   * Whether the error should be rendered or not using the application flash message
   */
  errorFlash?: boolean
  /**
   * A custom error message that is displayed instead of the incoming network error message.
   */
  errorMessage?: string
}

interface LoadingStateInterface {
  /**
   * Indicates whether the query is actively fetching data. This property is useful for showing network activity
   * indicators that do not block the entire user interface. For UI-blocking loading states, use the `loading` property
   * as it can provide cached data for a better user experience.
   */
  networkActivity: boolean

  /**
   * Indicates if there's no cached data for this query. Returns `true` when there's no data present in cache for the
   * query and no network request is in progress. Returns `false` if cached data is present or if there are any errors.
   */
  loading: boolean
}

/**
 * This custom hook type augments the return type of Apollo's `useQuery` hook with two additional properties:
 * - networkActivity: Indicates if the query is actively fetching data. It's ideal for showing network activity indicators.
 * - loading: Indicates if there's no cached data for the query and a network request is in progress.
 *
 * @template Query The GraphQL query type.
 * @template Variables The variables for the GraphQL query, must be an object with string keys and unknown values.
 */
export type UseQueryType<Query, Variables extends { [key: string]: unknown }> = ReturnType<
  typeof apolloUseQuery<Query, Variables>
> &
  LoadingStateInterface

type LazyQueryCallback<Query, Variables extends { [key: string]: unknown }> = ReturnType<
  typeof apolloUseLazyQuery<Query, Variables>
>[0]

type UseLazyQueryType<Query, Variables extends { [key: string]: unknown }> = [
  LazyQueryCallback<Query, Variables>,
  ReturnType<typeof apolloUseLazyQuery<Query, Variables>>[1] & LoadingStateInterface
]

const handleFlash = (error: QueryResult['error'], extra: ExtraOptions) => {
  const { errorFlash, errorMessage } = extra

  if (errorFlash) {
    app.setFlash({
      type: 'wrong',
      message: errorMessage ?? error?.networkError?.message ?? i18n.t('errors.generic_error'),
      time: 3000,
    })
  }
}

/**
 * This function is a custom implementation of Apollo's `useQuery` hook, tailored for use within Factorial. It adds
 * default behavior that is opinionated towards Factorial's needs. This includes additional loading states that provide
 * more detailed information about the query's progress, and also improved error handling.
 *
 * @template Query The GraphQL query type.
 * @template Variables The variables for the GraphQL query, must be an object with string keys and unknown values.
 *
 * @param {TypedDocumentNode<Query, Variables>} document The GraphQL query document.
 * @param {...args} args The query hook options, may include variables for the query and extra params used to handle errors.
 *
 * @returns {UseQueryType<Query, Variables>} The query result along with additional properties `networkActivity` and `loading`.
 */
export const useQuery = <Query, Variables extends { [key: string]: unknown }>(
  document: TypedDocumentNode<Query, Variables>,
  ...args: Record<string, never> extends Variables
    ? [
        options?: Omit<QueryHookOptions<Query, Variables>, 'variables'> & {
          variables?: Exact<Variables>
        } & {
          extra?: ExtraOptions
        }
      ]
    : [
        options: Omit<QueryHookOptions<Query, Variables>, 'variables'> & {
          variables: Exact<Variables>
        } & {
          extra?: ExtraOptions
        }
      ]
): UseQueryType<Query, Variables> => {
  const result = apolloUseQuery(document, ...args)

  // Handle network errors gracefully rendering a flash message
  if (args[0] && args[0].extra) {
    const { errorFlash, errorMessage } = args[0].extra

    if (errorFlash) {
      app.setFlash({
        type: 'wrong',
        message:
          errorMessage ?? result.error?.networkError?.message ?? i18n.t('errors.generic_error'),
        time: 3000,
      })
    }
  }

  return {
    ...result,
    // Set loading to true when there's no data and no error
    loading: !result.data && !result.error && !args[0]?.skip,
    // Set networkActivity to the loading state of the query
    networkActivity: result.loading,
  }
}

/**
 * This function is a custom implementation of Apollo's `useLazyQuery` hook, tailored for use within Factorial. It adds
 * default behavior that is opinionated towards Factorial's needs. This includes additional loading states that provide
 * more detailed information about the query's progress, and also improved error handling.
 *
 * @template Query The GraphQL query type.
 * @template Variables The variables for the GraphQL query, must be an object with string keys and unknown values.
 *
 * @param {TypedDocumentNode<Query, Variables>} document The GraphQL query document.
 * @param {...args} args The query hook options, may include variables for the query and extra params used to handle errors.
 *
 * @returns [LazyQueryCallback<Query, Variables>, {UseQueryType<Query, Variables>}] The query
 * callback and its result along with additional properties `networkActivity` and `loading`.
 */
export const useLazyQuery = <Query, Variables extends { [key: string]: unknown }>(
  document: TypedDocumentNode<Query, Variables>,
  ...args: [
    options?: Omit<QueryHookOptions<Query, Variables>, 'variables'> & {
      variables?: Exact<Variables>
    } & {
      extra?: ExtraOptions
    }
  ]
): UseLazyQueryType<Query, Variables> => {
  const [fetcher, result] = apolloUseLazyQuery(document, ...args)
  if (args[0]?.extra) handleFlash(result.error, args[0].extra)

  return [
    fetcher,
    {
      ...result,
      // Set loading to true when there's no data and no error
      loading: !result.data && !result.error && !args[0]?.skip,
      // Set networkActivity to the loading state of the query
      networkActivity: result.loading,
    },
  ]
}
